// src/components/assistant/MessageDisplay.js
import React, { useRef, useEffect, useState, useMemo } from 'react';
import { List, ListItem, Typography, CircularProgress, Chip, Box, Stack, Paper, Button, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import BarChartComponent from '../charts/BarChart';
import LineChartComponent from '../charts/LineChart';
import PieChartComponent from '../charts/PieChart';
import BoxAndWhiskerChartComponent from '../charts/BoxAndWhiskerChart';
import ScatterChartComponent from '../charts/ScatterChart';
import SuperChartComponent from '../charts/SuperChart';
import ChatTable from './ChatTable';
import CodeIcon from '@mui/icons-material/Code';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DoneIcon from '@mui/icons-material/Done';
import ErrorIcon from '@mui/icons-material/Error';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import DescriptionIcon from '@mui/icons-material/Description';
import GridOnIcon from '@mui/icons-material/GridOn';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import { fetchGcsObject, getSignedUrl } from '../../services/api_utils';
import { toolState } from '../../utils/logicToolState';

const ChartRenderer = ({ chartGcsPath, liveChartData }) => {
  const [charts, setCharts] = useState(liveChartData || null);
  const [loading, setLoading] = useState(!liveChartData);
  const [error, setError] = useState(null);
  const chartsRef = useRef(charts);

  useEffect(() => {
    chartsRef.current = charts;
  }, [charts]);

  useEffect(() => {
    let cancelled = false;

    if (liveChartData) {
      setCharts(liveChartData);
      setError(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const loadCharts = async () => {
      if (!chartGcsPath) {
        if (!chartsRef.current) {
          setError("No chart path provided.");
        }
        setLoading(false);
        return;
      }
      try {
        // Keep rendering any existing live chart while the persisted version hydrates.
        if (!chartsRef.current) {
          setLoading(true);
        }
        const chartData = await fetchGcsObject(chartGcsPath);
        if (!cancelled) {
          setCharts(chartData);
          setError(null);
        }
      } catch (err) {
        if (!cancelled && !chartsRef.current) {
          setError("Failed to load chart data.");
        }
        console.error(err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadCharts();
    return () => {
      cancelled = true;
    };
  }, [chartGcsPath, liveChartData]);

  if (loading && !charts) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="300px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading chart...</Typography>
      </Box>
    );
  }

  if (error || !charts || !Array.isArray(charts)) {
    return <Typography color="error">Could not display chart. {error}</Typography>;
  }

  // The payload is a list (possibly nested lists) of charts. A combined chart
  // (`superchart`) is rendered as one — its title, its grouping and its order
  // — by the shared SuperChart renderer, never flattened into loose leaves.
  const flattenLists = (items) => (
    Array.isArray(items) ? items.flatMap(flattenLists) : [items]
  ).filter((item) => item && typeof item === 'object');

  const chartsToRender = flattenLists(charts);

  return (
    <>
      {chartsToRender.map((chartData, chartIndex) => (
        chartData.type === 'superchart' ? (
          <div key={`chart-${chartIndex}`} style={{ marginTop: '16px', width: '100%' }}>
            <SuperChartComponent chartData={chartData} />
          </div>
        ) : (
          <div key={`chart-${chartIndex}`} style={{ marginTop: '16px', width: '100%', height: '40vh', minHeight: '300px' }}>
            {chartData.type === 'bar' && <BarChartComponent chartData={chartData} />}
            {chartData.type === 'line' && <LineChartComponent chartData={chartData} />}
            {chartData.type === 'pie' && <PieChartComponent chartData={chartData} />}
            {chartData.type === 'box_and_whisker' && <BoxAndWhiskerChartComponent chartData={chartData} />}
            {chartData.type === 'scatter' && <ScatterChartComponent chartData={chartData} />}
          </div>
        )
      ))}
    </>
  );
};


const MessageList = styled(List)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}));

const ListItemStyled = styled(ListItem)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
}));

const AssistantMessage = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isFullscreen'
})(({ theme, color, isFullscreen }) => ({
  backgroundColor: color || '#7E58B5',
  color: color === '#F0B25A' ? '#000000' : '#FFFFFF',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  alignSelf: 'flex-start',
  maxWidth: isFullscreen ? '75%' : '90%',
  padding: theme.spacing(1, 1.5),
  width: isFullscreen ? '75%' : 'fit-content',
  minWidth: '50px',
}));

const UserMessage = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isFullscreen'
})(({ theme, isFullscreen }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  alignSelf: 'flex-end',
  maxWidth: isFullscreen ? '60%' : '70%',
  padding: theme.spacing(1, 1.5),
  width: 'fit-content',
}));

const ToolChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'toolName',
})(({ theme, toolName }) => ({
  margin: theme.spacing(0.5, 0),
  backgroundColor: toolName === 'implement_node_logic'
    ? 'rgba(126, 88, 181, 0.38)'
    : toolName === 'explore_python'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.7)',
  border: toolName === 'implement_node_logic'
    ? '1px solid rgba(178, 137, 235, 0.7)'
    : '1px solid transparent',
  color: theme.palette.common.white,
  maxWidth: '100%',
  '& .MuiChip-label': {
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    textOverflow: 'clip',
  },
  '& .MuiChip-icon': {
    color: theme.palette.grey[300],
  },
  '& .MuiChip-iconColorError': {
    color: '#f44336 !important',
  },
  '& .MuiChip-iconColorSuccess': {
    color: '#4caf50 !important',
  },
}));

const CursorBlink = styled('span')(({ theme }) => ({
  display: 'inline-block',
  width: '8px',
  height: '1.2em',
  backgroundColor: 'currentColor',
  marginLeft: '2px',
  animation: 'blink 1s step-end infinite',
  '@keyframes blink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0 },
  },
}));

const ThinkingDot = styled('span')({
  width: 4,
  height: 4,
  borderRadius: '50%',
  backgroundColor: 'currentColor',
  animation: 'dotPulse 1.4s ease-in-out infinite',
  '@keyframes dotPulse': {
    '0%, 60%, 100%': { opacity: 0.3 },
    '30%': { opacity: 1 },
  },
});

const ThinkingIcon = styled(AutoAwesomeIcon)({
  fontSize: 16,
  animation: 'iconPulse 2s ease-in-out infinite',
  '@keyframes iconPulse': {
    '0%, 100%': { opacity: 0.6 },
    '50%': { opacity: 1 },
  },
});

const ThinkingIndicator = ({ thinkingText }) => {
  const [expanded, setExpanded] = useState(false);
  const hasText = !!thinkingText;

  return (
    <Box sx={{ py: 0.5, color: 'rgba(255, 255, 255, 0.7)' }}>
      <Box
        display="flex"
        alignItems="center"
        gap={0.75}
        onClick={() => hasText && setExpanded(prev => !prev)}
        sx={{ cursor: hasText ? 'pointer' : 'default', userSelect: 'none' }}
      >
        <ThinkingIcon />
        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'inherit' }}>
          Thinking
        </Typography>
        <Box display="flex" gap="3px" alignItems="center">
          <ThinkingDot sx={{ animationDelay: '0s' }} />
          <ThinkingDot sx={{ animationDelay: '0.2s' }} />
          <ThinkingDot sx={{ animationDelay: '0.4s' }} />
        </Box>
        {hasText && (
          expanded
            ? <ExpandLessIcon sx={{ fontSize: 16, ml: 0.5 }} />
            : <ExpandMoreIcon sx={{ fontSize: 16, ml: 0.5 }} />
        )}
      </Box>
      {expanded && hasText && (
        <Typography
          variant="body2"
          sx={{
            mt: 0.75,
            pl: 3.25,
            color: 'rgba(255, 255, 255, 0.5)',
            fontStyle: 'italic',
            fontSize: '0.8rem',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
          }}
        >
          {thinkingText}
        </Typography>
      )}
    </Box>
  );
};

const MarkdownContent = styled(ReactMarkdown)(({ theme }) => ({
  color: 'inherit',
  fontSize: theme.typography.body1.fontSize,
  fontFamily: theme.typography.fontFamily,
  wordWrap: 'break-word',
  '& > *:first-of-type': { marginTop: 0 },
  '& > *:last-of-type': { marginBottom: 0 },
  '& p': { margin: theme.spacing(0, 0, 1, 0) },
  '& a': { color: 'inherit', textDecoration: 'underline' },
  '& code:not(pre code)': {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    padding: '0.2em 0.4em',
    borderRadius: '3px',
    fontFamily: 'monospace',
    fontSize: '85%',
  },
  '& pre': {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    padding: theme.spacing(1),
    borderRadius: '4px',
    overflowX: 'auto',
    fontFamily: 'monospace',
    margin: theme.spacing(1, 0),
  },
  '& pre code': {
    backgroundColor: 'transparent',
    padding: 0,
    fontSize: '100%',
    fontFamily: 'inherit',
  },
  '& ul, & ol': { paddingLeft: theme.spacing(3), margin: theme.spacing(1, 0) },
  '& li': { marginBottom: theme.spacing(0.5) },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    margin: theme.spacing(1, 0),
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
  '& th, & td': {
    border: '1px solid rgba(255, 255, 255, 0.2)',
    padding: theme.spacing(0.5, 1),
    textAlign: 'left',
  },
  '& th': { fontWeight: 'bold' },
}));

const getToolIcon = (toolState) => {
  if (!toolState) return <CodeIcon />;
  if (toolState.toLowerCase().includes('error')) return <ErrorIcon color="error" />;
  if (toolState.toLowerCase().includes('starting')) return <CodeIcon />;
  if (toolState.toLowerCase().includes('executing') || toolState.toLowerCase().includes('processing')) return <PlayArrowIcon />;
  if (toolState.toLowerCase().includes('executed') || toolState.toLowerCase().includes('completed')) return <DoneIcon color="success" />;
  return <CodeIcon />;
};

const getToolColor = (toolState) => {
  if (!toolState) return 'default';
  if (toolState.toLowerCase().includes('error')) return 'error';
  if (toolState.toLowerCase().includes('executed') || toolState.toLowerCase().includes('completed')) return 'success';
  return 'default';
};

const getAttachmentIcon = (filename = '', kind = '') => {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (kind === 'image' || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return <ImageIcon />;
  if (kind === 'pdf' || ext === 'pdf') return <PictureAsPdfIcon />;
  if (['xlsx', 'xls'].includes(ext)) return <TableChartIcon />;
  if (ext === 'docx') return <DescriptionIcon />;
  if (ext === 'csv') return <GridOnIcon />;
  return <InsertDriveFileIcon />;
};

const downloadBlob = (blob, filename) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename || 'download';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

const CANVAS_SUPPORTED_EXTENSIONS = ['.xlsx', '.docx'];

const FileDownloadCard = ({ filename, description, downloadUrl, gcsPath, sandboxPath, toolUseId, onShowInCanvas }) => {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const ext = '.' + (filename || '').split('.').pop().toLowerCase();
  const canvasSupported = CANVAS_SUPPORTED_EXTENSIONS.includes(ext);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      setError(null);
      const resolvedUrl = downloadUrl || (gcsPath ? await getSignedUrl(gcsPath) : null);
      if (!resolvedUrl) {
        throw new Error('No download URL available.');
      }

      const response = await fetch(resolvedUrl);
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      const blob = await response.blob();
      downloadBlob(blob, filename);
    } catch (err) {
      console.error('Failed to download assistant file:', err);
      setError(err.message || 'Download failed.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        color: '#FFFFFF',
        borderColor: 'rgba(255, 255, 255, 0.12)',
        borderRadius: 1.5,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          color: '#E9EEF8',
          flexShrink: 0,
        }}
      >
        {getAttachmentIcon(filename)}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ wordBreak: 'break-word', color: '#FFFFFF' }}>{filename}</Typography>
        {description && (
          <Typography variant="body2" sx={{ wordBreak: 'break-word', color: 'rgba(255, 255, 255, 0.72)' }}>
            {description}
          </Typography>
        )}
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {canvasSupported && onShowInCanvas && (
          <Tooltip title="Show in canvas">
            <IconButton
              size="small"
              onClick={() => onShowInCanvas({ filename, downloadUrl, gcsPath, sandboxPath, toolUseId })}
              sx={{
                color: '#FFFFFF',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.16)',
                },
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleDownload}
          disabled={downloading}
          startIcon={<DownloadIcon />}
          sx={{
            color: '#FFFFFF',
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.14)',
            boxShadow: 'none',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.18)',
              boxShadow: 'none',
            },
          }}
        >
          {downloading ? 'Preparing...' : 'Download'}
        </Button>
      </Box>
    </Paper>
  );
};

const PersistedImagePart = ({ part }) => {
  const [resolvedAssets, setResolvedAssets] = useState([]);
  const [loading, setLoading] = useState(Boolean(part?.image_assets?.length));
  const [error, setError] = useState(null);
  const resolvedAssetsRef = useRef(resolvedAssets);
  const assetSignature = useMemo(
    () => JSON.stringify(
      (part?.image_assets || []).map((asset) => ({
        gcs_path: asset.gcs_path,
        page_number: asset.page_number || null,
      }))
    ),
    [part?.image_assets]
  );

  useEffect(() => {
    resolvedAssetsRef.current = resolvedAssets;
  }, [resolvedAssets]);

  useEffect(() => {
    let cancelled = false;

    const loadAssets = async () => {
      if (!part?.image_assets?.length) {
        setResolvedAssets([]);
        setLoading(false);
        return;
      }

      try {
        if (!resolvedAssetsRef.current.length) {
          setLoading(true);
        }
        setError(null);
        const nextAssets = await Promise.all(
          part.image_assets.map(async (asset) => ({
            ...asset,
            url: await getSignedUrl(asset.gcs_path),
          }))
        );
        if (!cancelled) {
          setResolvedAssets(nextAssets);
        }
      } catch (err) {
        console.error('Failed to load persisted image attachment:', err);
        if (!cancelled && !resolvedAssetsRef.current.length) {
          setError('Failed to load attachment preview.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAssets();
    return () => {
      cancelled = true;
    };
  }, [assetSignature]);

  if (!part?.image_assets?.length) {
    return null;
  }

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
        {part.filename}
      </Typography>
      {part.kind === 'pdf' && Number(part.total_page_count || 0) > 20 && (
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
          PDF contents truncated due to size.
        </Typography>
      )}
      {loading && !resolvedAssets.length && (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} sx={{ color: 'inherit' }} />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            Loading attachment preview...
          </Typography>
        </Box>
      )}
      {error && !resolvedAssets.length && (
        <Typography variant="caption" color="error.light">
          {error}
        </Typography>
      )}
      {resolvedAssets.map((asset, index) => (
        <Box
          key={`${asset.gcs_path || 'asset'}-${index}`}
          sx={{
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.16)',
          }}
        >
          <Box
            component="img"
            src={asset.url}
            alt={part.kind === 'pdf' && asset.page_number ? `${part.filename} page ${asset.page_number}` : part.filename || 'Attachment'}
            sx={{
              display: 'block',
              width: '100%',
              maxHeight: 360,
              objectFit: 'contain',
              backgroundColor: 'rgba(255,255,255,0.04)',
            }}
          />
          {part.kind === 'pdf' && asset.page_number && (
            <Typography variant="caption" sx={{ display: 'block', px: 1, py: 0.5, color: 'rgba(255,255,255,0.8)' }}>
              Page {asset.page_number}
            </Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
};

function MessageDisplay({ messages, loading, color, autoScrollEnabled = true, isFullscreen = false, onShowInCanvas }) {
  const messagesEndRef = useRef(null);
  const prevScrollKeyRef = useRef('');

  // Only scroll when message count changes or the last message's text content grows,
  // not on every array-reference change (which fires on unrelated rerenders).
  useEffect(() => {
    if (!autoScrollEnabled) return;
    const last = messages[messages.length - 1];
    const textLen = last?.content?.reduce((n, p) => n + (p.text?.length || 0), 0) || 0;
    const partCount = last?.content?.length || 0;
    const scrollKey = `${messages.length}-${textLen}-${partCount}`;
    if (scrollKey !== prevScrollKeyRef.current) {
      prevScrollKeyRef.current = scrollKey;
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  }, [messages, autoScrollEnabled]);

  const getTextContent = (content) => {
    if (!content) return '';
    return content.filter(part => part.type === 'text').map(part => part.text).join('');
  };

  return (
    <MessageList>
      {messages.map((message, index) => (
        <ListItemStyled
          key={message.id || index}
          style={{ alignItems: message.role === 'user' ? 'flex-end' : 'flex-start' }}
        >
          {message.role === 'user' ? (
            <UserMessage isFullscreen={isFullscreen}>
              {!!getTextContent(message.content) && (
                <MarkdownContent>{getTextContent(message.content)}</MarkdownContent>
              )}
              {(message.content?.some((part) => (
                part.type === 'file_upload' ||
                (part.type === 'images' && (!part.image_assets || part.image_assets.length === 0))
              ))) && (
                <Stack direction="row" spacing={1} sx={{ mt: getTextContent(message.content) ? 1 : 0, flexWrap: 'wrap', gap: 1 }}>
                  {message.content
                    ?.filter((part) => (
                      part.type === 'file_upload' ||
                      (part.type === 'images' && (!part.image_assets || part.image_assets.length === 0))
                    ))
                    .map((part, i) => (
                      <Chip
                        key={`attachment-${i}`}
                        icon={getAttachmentIcon(part.filename, part.kind)}
                        label={part.filename || 'Attachment'}
                        size="small"
                        variant="outlined"
                        sx={{
                          color: 'inherit',
                          borderColor: 'rgba(255,255,255,0.4)',
                          '& .MuiChip-icon': { color: 'inherit' },
                        }}
                      />
                    ))}
                </Stack>
              )}
              {message.content
                ?.filter((part) => part.type === 'images')
                .map((part, i) => (
                  <PersistedImagePart key={`image-part-${i}`} part={part} />
                ))}
              {message.content
                ?.filter((part) => part.type === 'canvas_edit')
                .map((part, i) => (
                  <Paper key={`canvas-edit-${i}`} variant="outlined" sx={{ p: 1, mt: 1, display: 'flex', alignItems: 'center', gap: 1, borderColor: 'rgba(255,255,255,0.3)' }}>
                    <EditIcon fontSize="small" sx={{ color: 'inherit', opacity: 0.7 }} />
                    <Typography variant="body2" sx={{ color: 'inherit', opacity: 0.8 }}>
                      Edited canvas: {part.source_filename} ({part.edit_count || part.edits?.length || 0} edit{(part.edit_count || part.edits?.length || 0) !== 1 ? 's' : ''})
                    </Typography>
                  </Paper>
                ))}
            </UserMessage>
          ) : (
            <AssistantMessage color={color} isFullscreen={isFullscreen}>
              <Stack spacing={1} width="100%">
                {message.content?.map((part, i) => {
                  if (part.type === 'text') {
                    return ( <Box key={`text-${i}`}><MarkdownContent>{part.text || ''}</MarkdownContent></Box> );
                  } else if (part.type === 'tool_use') {
                    // A durable Logic Assistant part records the event that
                    // ended it, so its label is derived here exactly as the
                    // live stream derived it (L57). A part without one — the
                    // Decode Agent's tools, or a transcript persisted before
                    // the field existed — shows the label it was stored with.
                    const toolName = part.tool || part.name;
                    const label = part.event
                      ? toolState(toolName, part.event, part.state)
                      : (part.state || `Using ${part.name}`);
                    return (
                      <ToolChip
                        key={part.id || `tool-${i}`}
                        toolName={toolName}
                        icon={getToolIcon(label)}
                        label={label}
                        size="small"
                        color={['explore_python', 'implement_node_logic'].includes(toolName)
                          ? 'default'
                          : getToolColor(label)}
                      />
                    );
                  } else if (part.type === 'charts') {
                      return <ChartRenderer key={`chart-renderer-${i}`} liveChartData={part.charts_data} chartGcsPath={part.charts_gcs_path} />;
                  } else if (part.type === 'table') {
                      return <ChatTable key={`table-${i}`} liveTableData={part.table_data} tableGcsPath={part.table_gcs_path} />;
                  } else if (part.type === 'file') {
                      return (
                        <FileDownloadCard
                          key={`file-${i}`}
                          filename={part.filename}
                          description={part.description}
                          downloadUrl={part.download_url}
                          gcsPath={part.file_gcs_path}
                          sandboxPath={part.sandbox_path}
                          toolUseId={part.tool_use_id}
                          onShowInCanvas={onShowInCanvas}
                        />
                      );
                  }
                  return null;
                })}
                {message.streaming && message.compacting && (
                  <Box sx={{ py: 0.5, color: 'rgba(255, 255, 255, 0.7)' }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <ThinkingIcon />
                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'inherit' }}>
                        Compacting conversation
                      </Typography>
                      <Box display="flex" gap="3px" alignItems="center">
                        <ThinkingDot sx={{ animationDelay: '0s' }} />
                        <ThinkingDot sx={{ animationDelay: '0.2s' }} />
                        <ThinkingDot sx={{ animationDelay: '0.4s' }} />
                      </Box>
                    </Box>
                  </Box>
                )}
                {message.streaming && !message.compacting && (message.thinking || !getTextContent(message.content)) && (
                  <ThinkingIndicator thinkingText={message.thinkingText} />
                )}
                {message.streaming && !message.thinking && getTextContent(message.content) && (
                  <CursorBlink />
                )}
              </Stack>
            </AssistantMessage>
          )}
        </ListItemStyled>
      ))}
      <div ref={messagesEndRef} />
    </MessageList>
  );
}

export default MessageDisplay;
